import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import LabelInput from "../../../../shared/components/labelInput";
import UploadFile from "../../../../shared/components/uploadFile";
import PortalBackButton from "../PortalBackButton";
import { postCase, sendClaimEmail } from "../../services/claimService";
import { useNavigate } from "fusion-plugin-react-router";
import { getForeName } from "../../../../shared/utils/userSessionUtils";
import { formatDateT } from "../../../../shared/utils/formatters";
import NewDateTimePicker from "../../../../shared/components/newDateTimePicker";
import QuoteFormWrapper from "../../../../shared/components/quoteFormWrapper";
import ReportClaimSuccess from "./ReportClaimSuccess";
import PageContentStack from "../../../../shared/components/PageContentStack";

const ReportClaimForm = () => {
  const {
    handleSubmit,
    watch,
    register,
    setValue,
    trigger,
    control,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      explaination: "",
      IncidentDateTime: null,
    },
    mode: "onChange",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userSession, setUserSession] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Check if form is valid including file upload requirement
  const isFormValid = useMemo(() => isValid && uploadedFiles.length > 0, [
    isValid,
    uploadedFiles,
  ]);
  const STATE = useAppStateStore();
  const navigate = useNavigate();
  const user = getForeName();
  const insurer = STATE.customerPolicies?.customerPolicies?.[0]?.insurer;
  const policyNumber =
    STATE.customerPolicies?.customerPolicies?.[0]?.policyNumber;

  useEffect(() => {
    const sessionData = sessionStorage.getItem("userSession");
    if (sessionData) {
      setUserSession(JSON.parse(sessionData));
    }
  }, []);

  const onSubmit = async () => {
    setLoading(true);
    setSuccess(false);

    // Convert uploaded files to Base64
    const attachments = [];
    for (const file of uploadedFiles) {
      try {
        const base64Content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]); // Remove data:image/jpeg;base64, prefix
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        attachments.push({
          name: file.name,
          type: file.type,
          base64Content,
          size: file.size,
        });
      } catch (error) {
        console.error("Error reading file:", error);
      }
    }

    const requestBody = {
      Incident_DateTime__c: formatDateT(watch("IncidentDateTime")),
      Incident_Description__c: watch("explaination"),
      policyNumber: policyNumber,
      Priority: "High",
      Origin: "Web",
      Type: "Claims",
      reasonForCase: "New Claim Reported",
      dateToAction: new Date().toISOString(),
      attachments: attachments, // Include Base64 encoded files
    };

    try {
      await sendClaimEmail({ requestBody, navigate, setLoading });
      setSuccess(true);
    } catch (e) {
      console.error("An error occurred while reporting the claim:", e);
      setLoading(false);
    }
  };

  if (success) {
    return <ReportClaimSuccess onBack={() => navigate("/landing")} />;
  }

  return (
    <PageContentStack>
      <QuoteFormWrapper>
        <h1 className="tw-mb-2.5">Report a Claim</h1>
        <p className={`tw-text-[#4b4b4b] ${!insurer ? "tw-mb-[30px]" : ""}`}>
            Hi <span className="tw-capitalize">{user}</span>, please complete the
          form below to report a claim. Our team will be in touch with you as
          soon as possible.
        </p>

        {insurer === "Sabre Insurance Company Ltd" && (
          <p className="tw-mt-[5px] tw-mb-[5px] tw-block tw-text-sm tw-text-dis-error tw-mb-[30px]">
            If you require roadside assistance. Please call 01621 737253 as soon
            as possible
          </p>
        )}

        {insurer === "AXA Insurance UK PLC" && (
          <p className="tw-mt-[5px] tw-mb-[5px] tw-block tw-text-sm tw-text-dis-error tw-mb-[30px]">
            If you require roadside assistance. Please call 441737334952 as soon
            as possible
          </p>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <NewDateTimePicker
            name="IncidentDateTime"
            label="Date and time of the accident"
            errors={errors.IncidentDateTime}
            setValue={setValue}
            control={control}
            rules={{ required: "Date is required" }}
            watch={watch}
            defaultValue={watch("IncidentDateTime")}
            maxDate={new Date()}
          />
          <LabelInput
            id="explaination"
            label="Brief explanation of the accident"
            type="textarea"
            placeholder="Enter details"
            register={register("explaination", {
              required: "Comments are required",
            })}
            errors={errors.explaination}
            watch={watch("explaination")}
            trigger={trigger}
            maxLength={500}
          />
          <UploadFile
            id="explaination"
            label="Upload images of the accident below"
            errors={errors.explaination}
            trigger={trigger}
            maxLength={500}
            onFilesUploaded={setUploadedFiles}
          />
          {uploadedFiles.length === 0 && (
            <span className="tw-mt-[5px] tw-mb-[5px] tw-block tw-text-sm tw-text-dis-error">At least one image is required</span>
          )}
          <button
            type="submit"
            className={`tw-mt-[15px] tw-block tw-w-full tw-cursor-pointer tw-rounded-dis-pill tw-border-2 tw-border-dis-sky tw-bg-dis-sky tw-px-2.5 tw-py-3.5 tw-text-center tw-text-base tw-font-bold tw-text-dis-navy tw-no-underline  [caret-color:transparent] hover:tw-border-dis-sky-hover hover:tw-bg-dis-sky-hover${!isFormValid ? " tw-opacity-20 tw-z-[-1]" : ""}`}
            disabled={loading || !isFormValid}
          >
            {loading ? "Submitting..." : "Submit Claim Details"}
          </button>
        </form>
      </QuoteFormWrapper>
      <PortalBackButton />
    </PageContentStack>
  );
};

export default ReportClaimForm;
